import { useEffect, useState } from 'react';
import axios from 'axios';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Sparkles, Loader2, Lock, Trophy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * FeatureFlagsPanel — admin control to enable/disable AI feature unlocks.
 * Shows current member count vs threshold and toggles persisted state.
 */
export default function FeatureFlagsPanel() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [pulse, setPulse] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [flagsRes, pulseRes] = await Promise.all([
        axios.get(`${API}/api/community/admin/flags`),
        axios.get(`${API}/api/community/pulse`),
      ]);
      setFlags(flagsRes.data);
      setPulse(pulseRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load flags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (flag) => {
    try {
      setSaving(flag.id);
      await axios.post(`${API}/api/community/admin/flags/${flag.id}`, {
        enabled: !flag.enabled,
      });
      toast.success(`${flag.label} ${!flag.enabled ? 'enabled' : 'disabled'}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update flag');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );
  }

  const members = pulse?.metrics?.members ?? 0;

  return (
    <div className="space-y-3" data-testid="feature-flags-panel">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-500" />
        <h3 className="font-semibold">AI Feature Unlocks</h3>
        <span className="text-xs text-muted-foreground ml-auto">
          {members.toLocaleString()} members
        </span>
      </div>
      <div
        className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900"
        data-testid="ai-flags-untested-warning"
      >
        <strong>AI features are untested.</strong> They are disabled
        site-wide pending QA. Do not enable on production until the AI
        translation, content generation, and health-insight flows have
        been integration-tested end-to-end.
      </div>
      {flags.map((f) => {
        const unlocked = members >= f.threshold;
        const pct = Math.min(100, Math.round((members / f.threshold) * 100));
        return (
          <div
            key={f.id}
            className="p-3 border rounded-lg flex items-start gap-3"
            data-testid={`flag-row-${f.id}`}
          >
            {f.enabled ? (
              <Sparkles className="w-4 h-4 text-emerald-500 mt-0.5" />
            ) : unlocked ? (
              <Trophy className="w-4 h-4 text-amber-500 mt-0.5" />
            ) : (
              <Lock className="w-4 h-4 text-muted-foreground mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium text-sm">{f.label}</div>
                <span className="text-xs text-muted-foreground">
                  ({members}/{f.threshold})
                </span>
              </div>
              {f.description && (
                <p className="text-xs text-muted-foreground mt-0.5">{f.description}</p>
              )}
              <div className="h-1 w-full rounded-full bg-muted mt-1.5 overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    f.enabled ? 'bg-emerald-500' : unlocked ? 'bg-amber-500' : 'bg-primary/60'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
            <Switch
              checked={f.enabled}
              onCheckedChange={() => toggle(f)}
              disabled={saving === f.id}
              data-testid={`flag-toggle-${f.id}`}
              aria-label={`Toggle ${f.label}`}
            />
          </div>
        );
      })}
      <p className="text-xs text-muted-foreground">
        Flags unlock automatically at the threshold but stay disabled until you turn them on here.
      </p>
    </div>
  );
}
